import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import * as actions from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.actions';
import { FieldUpdateHandlerParam } from 'shared/form';
import * as types from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.types';
import * as accessFunctionActions from 'data/accessFunction/accessFunction.actions';
import * as warrantActions from 'xcipio/warrants/warrant-actions';
import * as selectors from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.selectors';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as templateSelectors from 'data/template/template.selectors';
import { FormTemplate, FormTemplateOption, FormTemplateSection } from 'data/template/template.types';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import {
  adaptFormDataForNEType,
  adaptInterfacemSectionFormData, adaptTemplateBasedOnSections, adaptTemplateForNEType, updateTemplateFieldsValues
} from 'routes/TargetDeliveryTemplateAccessFunction/utils/targetDeliveryTemplateFields.adapters';
import { adaptToForm } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.adapter';
import { TargetTemplateFormSection } from 'routes/TargetDeliveryTemplateAccessFunction/TargetDeliveryTemplateAccessFunction.types';
import { KeyValuePair } from 'data/types';
import * as _ from 'lodash';
import { getLeaOptions } from 'routes/UtimacoCircuitSwitch/utils/UtimacoCircuitSwitch.adapter';
import { AccessFunction } from 'data/accessFunction/accessFunction.types';
import TargetDeliveryTemplateAccessFunctionForm from '../../components/TargetDeliveryTemplateAccessFunctionForm';
import { createTargetDeliveryTemplateBasedOnTemplate } from '../../utils/targetDeliveryTemplate.factory';
import {
  adaptTemplateOptions,
  adaptTemplateVisibilityMode,
  getTargetTemplateToSubmit,
  getAuditDetailsToSubmit,
} from '../../utils/targetDeliveryTemplate.adapter';
import targetTemplates from '../../templates';

type RouterParams = {
  history: string
}

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.TargetDeliveryTemplateAccessFunctionFormData
}

const mapState = (state: RootState) => ({
  targetDeliveryTemplate: selectors.getTargetDeliveryTemplateAccessFunction(state),
  selectedTargetDeliveryTemplate: selectors.getSelectedTargetDeliveryTemplateAccessFunction(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  deliveryFunctionId: state.global.selectedDF,
  privileges: state.authentication.privileges,
  beforeLogout: state.authentication.beforeLogout,
  accessFunctionTemplates: templateSelectors.getEnabledAccessFunctionFormTemplatesByKey(state),
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
});

const mapDispatch = {
  getTargetDeliveryTemplateAccessFunction: actions.getTargetDeliveryTemplateAccessFunction,
  goToTargetDeliveryTemplateAccessFunctionList: actions.goToTargetDeliveryTemplateAccessFunctionList,
  getAccessFunctions: accessFunctionActions.getAccessFunctionsWithViewOptions,
  goToTargetDeliveryTemplateAccessFunctionCreate: actions.goToTargetDeliveryTemplateAccessFunctionCreate,
  goToTargetDeliveryTemplateAccessFunctionEdit: actions.goToTargetDeliveryTemplateAccessFunctionEdit,
  goToTargetDeliveryTemplateAccessFunctionView: actions.goToTargetDeliveryTemplateAccessFunctionView,
  selectTargetDeliveryTemplateAccessFunction: actions.selectTargetDeliveryTemplateAccessFunction,
  getTargetDeliveryTemplateAccessFunctionList: actions.getTargetDeliveryTemplateAccessFunctionList,
  deleteTargetDeliveryTemplateAccessFunction: actions.deleteTargetDeliveryTemplateAccessFunction,
  updateTargetDeliveryTemplateAccessFunction: actions.updateTargetDeliveryTemplateAccessFunction,
  getSupportedFeature: warrantActions.getWarrantFormConfig,
  getTemplates: warrantActions.getAllTemplates,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>
export type Props = PropsFromRedux & PropsFromRouter;

export const TargetDeliveryTemplateAccessFunctionEdit = ({
  deliveryFunctionId,
  history,
  accessFunctions,
  enabledAccessFunctions,
  selectedTargetDeliveryTemplate,
  targetDeliveryTemplate,
  isAuditEnabled,
  userId,
  userName,
  goToTargetDeliveryTemplateAccessFunctionList,
  updateTargetDeliveryTemplateAccessFunction,
  getTargetDeliveryTemplateAccessFunction,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [templates, setTemplates] = useState<{ [key: string]: FormTemplate }>({});
  const [template, setTemplate] = useState<FormTemplate>();
  const [formData, setFormData] = useState<Partial<types.TargetDeliveryTemplateAccessFunctionFormData>>({});
  const { t } = useTranslation();

  useEffect(() => {
    const temp = selectedTargetDeliveryTemplate?.[0];

    if (!temp) {
      return;
    }

    getTargetDeliveryTemplateAccessFunction(deliveryFunctionId, temp.id as string);

  }, [
    deliveryFunctionId,
    selectedTargetDeliveryTemplate,
    getTargetDeliveryTemplateAccessFunction,
  ]);

  useEffect(() => {

    if (!targetDeliveryTemplate || !selectedTargetDeliveryTemplate || selectedTargetDeliveryTemplate.length <= 0) {
      return;
    }

    const newFormData = adaptToForm(targetDeliveryTemplate);

    const availableTemplates = adaptTemplateOptions(
      targetTemplates,
      accessFunctions,
      enabledAccessFunctions,
    );
    setTemplates(availableTemplates);

    const templateKey = targetDeliveryTemplate.afType ?? Object.keys(availableTemplates)[0];
    const adaptedTemplate = adaptTemplateVisibilityMode(availableTemplates[templateKey], 'edit');
    let newTemplate = cloneDeep(adaptedTemplate);
    if ((newFormData.targetInformation != null) && !_.isEmpty(newFormData.targetInformation) && (newFormData.targetInformation[0].ACC != null)) {
      newTemplate = adaptTemplateBasedOnSections(adaptedTemplate, 'ACC', (newFormData.targetInformation[0].ACC as string), 'targetInformation');
    }

    if ((newTemplate as FormTemplate).key === 'SIEMENS_LIMS') {
      const chosenAccessFunction = accessFunctions.find((af) => af.id === newFormData.afId);
      const leaOptions = getLeaOptions(chosenAccessFunction as AccessFunction);
      ((newTemplate as FormTemplate).ICD as FormTemplateSection).fields.LEA.options = leaOptions;
      ((newTemplate as FormTemplate).ICD as FormTemplateSection).fields.LEA.initial = (newFormData?.ICD as types.TargetDeliveryTemplateInterfaceFormData[])[0].LEA;

      // Get ACC from selected template
      if (selectedTargetDeliveryTemplate?.length > 0) {
        const targetInfoSection = selectedTargetDeliveryTemplate[0]
          .templateSections?.find((section) => section.name === 'targetInformation')
        const acc: string = targetInfoSection?.options.find((option) => option.key === 'ACC')?.value as string;

        // Update Network-Call Conten type
        const networkSection = selectedTargetDeliveryTemplate[0]
          .templateSections?.find((section) => section.name === 'networkType')
        networkSection?.options.forEach((network) => {
          ((newTemplate as FormTemplate).networkType as FormTemplateSection).fields[network.key].initial = network.value
        })
        updateTemplateFieldsValues(newTemplate as FormTemplate, acc);
      }

      (newTemplate as FormTemplate).formId = Date.now().toString();
    }

    if (targetDeliveryTemplate.templateSections != null && targetDeliveryTemplate.afType === 'GENBANDC20') {
      const sections = targetDeliveryTemplate.templateSections.filter(
        (section) => section.name === 'callContentChannels',
      );
      if (sections != null) {

        const sanreq = sections[0].options?.find((option) => option.key === 'SANREQ');
        const prependrtedigs = sections[0].options?.find((option) => option.key === 'PREPENDRTEDIGS');

        if (sanreq != null) {
          newTemplate = adaptTemplateBasedOnSections(newTemplate, 'SANREQ', (sanreq?.value as string), 'callContentChannels');
        }
        if (prependrtedigs != null) {
          newTemplate = adaptTemplateBasedOnSections(newTemplate, 'PREPENDRTEDIGS', (prependrtedigs?.value as string), 'callContentChannels');
        }
      }
    }

    if ((newTemplate as FormTemplate).key === 'ERICSSON_NE') {
      const general = (newFormData.templateSections || []).find((section) => section.name === 'EIMSGENERAL');
      const neType = (general?.options || []).find((o) => o.key === 'netype');
      newTemplate = adaptTemplateForNEType(newTemplate as FormTemplate, neType?.value as string || '')
    }

    if ((newTemplate as FormTemplate).key === 'RIBBONC20_CSC') {
      const ribbonTemplate = newTemplate as FormTemplate;
      const usnbdCallContentChannelInformation = (newFormData.templateSections || [])
        .find((section) => section.name === 'usnbdCallContentChannelInformation');
      const definition = String((((usnbdCallContentChannelInformation?.options || []) as any[])
        .find((option: any) => option.key === 'definition')?.value) ?? '');

      if (definition === 'PAIRED') {
        (ribbonTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dndest.label = 'Associate\'s DN';
        (ribbonTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dnsrc.hide = 'false';
      } else {
        (ribbonTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dndest.label = 'Combined DN';
        (ribbonTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dnsrc.hide = 'true';
      }
    }

    setFormData(newFormData);
    setTemplate(newTemplate);

  }, [
    accessFunctions,
    enabledAccessFunctions,
    selectedTargetDeliveryTemplate,
    targetDeliveryTemplate,
  ]);

  const handleFieldUpdate = (
    { name, value }: FieldUpdateHandlerParam,
    formSection: TargetTemplateFormSection,
    interfaceId?: string,
  ) => {

    let clonedData = cloneDeep(formData);

    let clonedTemplate = cloneDeep(template);
    setFormData({
      ...clonedData,
      [name]: value,
    });

    if (formSection === 'main') {
      if (name === 'afId' && (clonedTemplate as FormTemplate).key === 'SIEMENS_LIMS') {
        const chosenAccessFunction = accessFunctions.find((af) => af.id === value);
        const leaOptions = getLeaOptions(chosenAccessFunction as AccessFunction);
        ((clonedTemplate as FormTemplate).ICD as FormTemplateSection).fields.LEA.options = leaOptions;
        ((clonedTemplate as FormTemplate).ICD as FormTemplateSection).fields.LEA.initial = leaOptions[0].value;
        (clonedTemplate as FormTemplate).formId = Date.now().toString();
      }

      if (name === 'afType') {
        const adaptedTemplate = adaptTemplateVisibilityMode(templates[value], 'edit');
        const newFormData = createTargetDeliveryTemplateBasedOnTemplate(
          adaptedTemplate,
        ) as types.TargetDeliveryTemplateAccessFunction;
        setFormData({
          ...newFormData,
          [name]: value,
        });
        setTemplate(cloneDeep(templates[value]));
        return;
      }

    } else {
      ((clonedTemplate as FormTemplate)[formSection] as FormTemplateSection).fields[name].initial = value;
      if (((clonedTemplate as FormTemplate).key === 'SIEMENS_LIMS')) {
        if (formSection === 'networkType' || formSection === 'callContentType' || name === 'ACC') {
          const acc = name === 'ACC' ? value : (clonedData.targetInformation as types.TargetDeliveryTemplateInterfaceFormData[])[0].ACC as string;
          updateTemplateFieldsValues(clonedTemplate as FormTemplate, acc);
        }
      }

      if ((clonedTemplate as FormTemplate).key === 'ERICSSON_NE' && name === 'netype') {
        clonedTemplate = adaptTemplateForNEType(clonedTemplate as FormTemplate, value)
        clonedData = adaptFormDataForNEType(clonedTemplate, clonedData)
      }

      if ((clonedTemplate as FormTemplate).key === 'RIBBONC20_CSC' && name === 'definition') {
        if (String(value) === 'PAIRED') {
          ((clonedTemplate as FormTemplate).usnbdCallContentChannelInformation as FormTemplateSection).fields.dndest.label = 'Associate\'s DN';
          ((clonedTemplate as FormTemplate).usnbdCallContentChannelInformation as FormTemplateSection).fields.dnsrc.hide = 'false';
        } else {
          ((clonedTemplate as FormTemplate).usnbdCallContentChannelInformation as FormTemplateSection).fields.dndest.label = 'Combined DN';
          ((clonedTemplate as FormTemplate).usnbdCallContentChannelInformation as FormTemplateSection).fields.dnsrc.hide = 'true';
        }
      }

      clonedData[formSection] = adaptInterfacemSectionFormData(
        clonedData[formSection],
        interfaceId,
        name,
        value,
      );

      clonedTemplate = adaptTemplateBasedOnSections(
        clonedTemplate,
        name,
        value,
        formSection,
      );

      (clonedTemplate as FormTemplate).formId = Date.now().toString();

      setFormData(clonedData);
    }
    setTemplate(clonedTemplate);

  };

  const getLeaOptions = (accessFunction: AccessFunction) => {
    let leaOptions: FormTemplateOption[] = [];
    if (accessFunction) {
      const leas: string[] = (accessFunction.dataInterfaces ?? [])
        .flatMap((df) => df.options)
        .filter((o) => ['lea', 'leacs', 'leaps'].includes(o.key))
        .map((o) => o.value as string);

      leaOptions = [
        ...leas.map((lea) => {
          return {
            label: lea,
            value: lea
          };
        })
      ];
    }
    return leaOptions;
  };

  const handleClickSave = () => {
    const isValid = formRef?.current?.isValid(true, true) ?? true;

    if (!isValid) {
      return;
    }

    const formDataValues = formRef?.current?.getValues();

    if (!formDataValues) {
      return;
    }
    const submitData = getTargetTemplateToSubmit(formDataValues);

    if (selectedTargetDeliveryTemplate?.[0]?.id) {

      const payload = {
        data: getTargetTemplateToSubmit(formDataValues),
        auditDetails: getAuditDetailsToSubmit(submitData, null, isAuditEnabled, userId, userName, 'UPDATE'),
      };

      updateTargetDeliveryTemplateAccessFunction(
        deliveryFunctionId,
        selectedTargetDeliveryTemplate[0].id,
        payload,
        history,
      );
    }
  };

  const handleCancel = () => goToTargetDeliveryTemplateAccessFunctionList(history);

  const handleInterfaceAdd = (formSection: TargetTemplateFormSection) => {
    const clonedData = cloneDeep(formData);

    const newData = createTargetDeliveryTemplateBasedOnTemplate(template, true);
    const newInterfaceData = (newData[formSection] as types.TargetDeliveryTemplateInterfaceFormData[])[0];
    (clonedData[formSection] as types.TargetDeliveryTemplateInterfaceFormData[]).push(newInterfaceData);

    setFormData(clonedData);
  };

  const handleInterfaceRemove = (tabId: string | number, formSection: TargetTemplateFormSection) => {
    const clonedData = cloneDeep(formData);

    clonedData[formSection] = clonedData[formSection]?.filter(({ id }) => id !== tabId);

    setFormData(clonedData);
  };

  if (!template) {
    return <Fragment />;
  }

  return (
    <div data-testid="TargetDeliveryTemplateAccessFunctionCreate">
      <TargetDeliveryTemplateAccessFunctionForm
        ref={formRef}
        isEditing
        template={template}
        data={formData}
        title={t('targetDeliveryTemplateAccessFunction.form.edit')}
        onInterfaceAdd={handleInterfaceAdd}
        onFieldUpdate={handleFieldUpdate}
        onClickSave={handleClickSave}
        onInterfaceRemove={handleInterfaceRemove}
        onClickCancelClose={handleCancel}
      />
    </div>
  );
};

export default withRouter(connector(TargetDeliveryTemplateAccessFunctionEdit));
